import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useSelector } from "react-redux";
import { MapContainer, TileLayer, useMap, useMapEvents } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import axiosInstance from "../../lib/auth/axiosApi";
import "./flockout.css";
import { FO_ABOUT } from "./paths";
import SkyLayers, { BASEMAPS, COLORS, ago } from "./SkyLayers";

// Polling like the Ledger: dijon-backend has no websockets, and 30s is plenty for "what flew over".
const POLL_MS = 30000;
const WINDOWS = [
  [24, "24h"],
  [24 * 7, "7d"],
  [24 * 30, "30d"],
];
function ClickTo({ onPick }) {
  useMapEvents({ click: (e) => onPick && onPick(e.latlng) });
  return null;
}

function FitOnce({ points }) {
  const map = useMap();
  const done = useRef(false);
  useEffect(() => {
    if (done.current || !points.length) return;
    done.current = true;
    if (points.length === 1) map.setView(points[0], 14);
    else map.fitBounds(points, { padding: [40, 40], maxZoom: 15 });
  }, [map, points]);
  return null;
}

function FlyTo({ target }) {
  const map = useMap();
  useEffect(() => {
    if (target) map.flyTo(target, Math.max(map.getZoom(), 15), { duration: 0.8 });
  }, [map, target]);
  return null;
}

function Toggle({ on, onClick, children }) {
  return (
    <button type="button" className="fo-chip" aria-pressed={on ? "true" : "false"} onClick={onClick}>
      {children}
    </button>
  );
}

function FlockoutMap() {
  const loggedIn = !!localStorage.getItem("access_token");
  const allowedCards = useSelector((state) => state.permissions.allowedCards);
  const allowed = allowedCards.includes("skywatch");

  const [data, setData] = useState({ nodes: [], drones: [], sky: [], cameras: [] });
  const [myNodes, setMyNodes] = useState([]);
  const [hours, setHours] = useState(24);
  const [basemap, setBasemap] = useState("dark");
  const [layers, setLayers] = useState({ drones: true, sky: true, alpr: true, mycams: true, nodes: true });
  const [pick, setPick] = useState(null); // {type: 'node'} | {type: 'camera', id}
  const [newKey, setNewKey] = useState(null);
  const [flyTo, setFlyTo] = useState(null);
  const [status, setStatus] = useState("Loading…");
  const [loadedAt, setLoadedAt] = useState(null);

  const load = useCallback(() => {
    axiosInstance
      .get(`skywatch/map/?hours=${hours}`)
      .then((res) => {
        setData(res.data);
        setLoadedAt(new Date().toISOString());
        setStatus(null);
      })
      .catch((err) =>
        setStatus(err.response && err.response.status === 429 ? "Slow down a sec. Retrying…" : "Couldn't reach the server. Retrying…")
      );
  }, [hours]);

  const loadNodes = useCallback(() => {
    axiosInstance.get("skywatch/nodes/").then((res) => setMyNodes(res.data)).catch(() => {});
  }, []);

  useEffect(() => {
    document.title = "Get The Flock Out My Sky Bitch";
  }, []);

  const member = loggedIn && allowed;

  useEffect(() => {
    load();
    if (member) loadNodes();
    const t = setInterval(() => {
      load();
      if (member) loadNodes();
    }, POLL_MS);
    return () => clearInterval(t);
  }, [member, load, loadNodes]);

  const fitPoints = useMemo(() => {
    const mine = data.nodes.filter((n) => n.mine).map((n) => [n.lat, n.lon]);
    return mine.length ? mine : data.nodes.map((n) => [n.lat, n.lon]);
  }, [data.nodes]);

  const onPick = (latlng) => {
    if (!pick) return;
    if (pick.type === "node") {
      const name = window.prompt("Name this box (only you see it)", "window box");
      if (!name) return setPick(null);
      axiosInstance
        .post("skywatch/nodes/", { name, latitude: latlng.lat, longitude: latlng.lng })
        .then((res) => {
          setNewKey({ name: res.data.name, key: res.data.api_key });
          loadNodes();
          load();
        })
        .catch(() => setStatus("Couldn't create the node."));
    } else if (pick.type === "camera") {
      axiosInstance
        .patch(`skywatch/cameras/${pick.id}/`, { latitude: latlng.lat, longitude: latlng.lng })
        .then(load)
        .catch(() => setStatus("Couldn't pin that camera."));
    }
    setPick(null);
  };

  const markAlpr = (cam) =>
    axiosInstance
      .patch(`skywatch/cameras/${cam.id}/`, { category: cam.category === "alpr" ? "consumer" : "alpr" })
      .then(load)
      .catch(() => setStatus("Couldn't update that camera."));

  const rotateKey = (node) => {
    if (!window.confirm(`New key for "${node.name}"? The old one stops working immediately.`)) return;
    axiosInstance
      .post(`skywatch/nodes/${node.id}/rotate_key/`)
      .then((res) => setNewKey({ name: node.name, key: res.data.api_key }))
      .catch(() => setStatus("Couldn't rotate the key."));
  };

  const copy = (text) => {
    if (navigator.clipboard) navigator.clipboard.writeText(text).catch(() => {});
  };

  const base = BASEMAPS[basemap];
  const unpinned = data.cameras.filter((c) => c.mine && c.latitude == null);
  const backendBase = axiosInstance.defaults.baseURL || "https://dijondreams.com/dijon-backend/api/";

  return (
    <div className="flockout fo-app">
      <div className="fo-bar">
        <h1>
          Get The Flock Out My Sky <span className="fo-bitch">Bitch</span>
        </h1>
        <Link to={FO_ABOUT} className="fo-chip" style={{ textDecoration: "none" }}>About &amp; kit</Link>
        <div className="fo-spacer" />
        {WINDOWS.map(([h, label]) => (
          <Toggle key={h} on={hours === h} onClick={() => setHours(h)}>{label}</Toggle>
        ))}
        <Toggle on={layers.drones} onClick={() => setLayers({ ...layers, drones: !layers.drones })}>Drones</Toggle>
        <Toggle on={layers.sky} onClick={() => setLayers({ ...layers, sky: !layers.sky })}>Unidentified</Toggle>
        <Toggle on={layers.alpr} onClick={() => setLayers({ ...layers, alpr: !layers.alpr })}>Plate readers</Toggle>
        {member && <Toggle on={layers.mycams} onClick={() => setLayers({ ...layers, mycams: !layers.mycams })}>My cameras</Toggle>}
        <Toggle on={layers.nodes} onClick={() => setLayers({ ...layers, nodes: !layers.nodes })}>Boxes</Toggle>
        <Toggle on={basemap === "satellite"} onClick={() => setBasemap(basemap === "dark" ? "satellite" : "dark")}>Satellite</Toggle>
      </div>

      <div className="fo-body">
        <div className={"fo-map" + (pick ? " fo-picking" : "")}>
          {pick && (
            <div className="fo-banner">
              {pick.type === "node" ? "Click where your box is" : "Click where the camera is"} ·{" "}
              <button type="button" className="fo-chip" onClick={() => setPick(null)}>cancel</button>
            </div>
          )}
          <MapContainer center={[39.5, -98.35]} zoom={4} scrollWheelZoom>
            <TileLayer key={basemap} url={base.url} attribution={base.attribution} maxZoom={19} />
            <TileLayer key={basemap + "-ref"} url={base.ref} maxZoom={19} />
            <ClickTo onPick={pick ? onPick : null} />
            <FitOnce points={fitPoints} />
            <FlyTo target={flyTo} />

            <SkyLayers data={data} layers={layers} />
          </MapContainer>

          <div className="fo-legend" aria-label="Legend">
            <div><span className="dot" style={{ background: COLORS.dji }} />DJI drone</div>
            <div><span className="dot" style={{ background: COLORS.other }} />Other drone</div>
            <div><span className="dot" style={{ background: COLORS.sky }} />Unidentified</div>
            <div><span className="dot" style={{ background: COLORS.alpr }} />Plate reader</div>
            <div><span className="dot" style={{ background: COLORS.node }} />Box</div>
          </div>
          <div className="fo-status">
            {status || `${data.drones.length} drones · ${data.sky.length} unidentified · updated ${ago(loadedAt)}`}
          </div>
        </div>

        <aside className="fo-side">
          <section>
            <h2>
              Drones <small>last {WINDOWS.find((w) => w[0] === hours)[1]}</small>
            </h2>
            {data.drones.length === 0 ? (
              <p className="fo-empty">Quiet skies. Nothing heard yet.</p>
            ) : (
              <ul className="fo-list">
                {data.drones.slice(0, 50).map((d) => {
                  const last = d.points[d.points.length - 1];
                  return (
                    <li key={d.serial}>
                      <button
                        type="button"
                        className="fo-rowbtn"
                        onClick={() => last && setFlyTo([last[0], last[1]])}
                        disabled={!last}
                      >
                        <span>
                          {d.serial} {d.events.some((e) => e.event === "repeat") && <span className="fo-pill crit">repeat</span>}
                        </span>
                        <span className="fo-meta">
                          {d.maker || "unknown"} · {ago(d.last_seen_at)} · {d.nodes.length} box{d.nodes.length === 1 ? "" : "es"}
                        </span>
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
          </section>

          {member ? (
            <>
          <section>
            <h2>
              My boxes <small>{myNodes.length}</small>
            </h2>
            {newKey && (
              <div className="fo-key">
                <b>Key for "{newKey.name}". Copy it now, it won't be shown again.</b>
                <code>{newKey.key}</code>
                <div>Paste it into your box's setup page. For a Pi node, add this to its config:</div>
                <pre>{`dijon:\n  enabled: true\n  url: ${backendBase}skywatch/ingest/\n  api_key: ${newKey.key}`}</pre>
                <div className="fo-actions">
                  <button type="button" className="fo-chip" onClick={() => copy(newKey.key)}>Copy key</button>
                  <button type="button" className="fo-chip" onClick={() => setNewKey(null)}>I saved it</button>
                </div>
              </div>
            )}
            <ul className="fo-list">
              {myNodes.map((n) => {
                const online = n.last_seen_at && Date.now() - new Date(n.last_seen_at).getTime() < 5 * 60 * 1000;
                return (
                  <li key={n.id}>
                    <button type="button" className="fo-rowbtn" onClick={() => setFlyTo([n.latitude, n.longitude])}>
                      <span>
                        {n.name} <span className={"fo-pill " + (online ? "on" : "off")}>{online ? "online" : "offline"}</span>
                      </span>
                      <span className="fo-meta">last check-in {ago(n.last_seen_at)}{n.firmware && ` · ${n.firmware}`}</span>
                    </button>
                    <div className="fo-actions">
                      <button type="button" className="fo-chip" onClick={() => rotateKey(n)}>New key</button>
                    </div>
                  </li>
                );
              })}
            </ul>
            <div className="fo-actions" style={{ marginTop: 8 }}>
              <button type="button" className="fo-chip" onClick={() => setPick({ type: "node" })}>+ Add a box</button>
            </div>
          </section>

          <section>
            <h2>
              Cameras my boxes heard <small>{unpinned.length} unpinned</small>
            </h2>
            {unpinned.length === 0 ? (
              <p className="fo-empty">None waiting. Boxes with the camera sniffer list what they hear here.</p>
            ) : (
              <ul className="fo-list">
                {unpinned.slice(0, 50).map((c) => (
                  <li key={c.id}>
                    <span>
                      {c.vendor || "unknown vendor"}{" "}
                      {c.category === "alpr" && <span className="fo-pill alpr">plate reader</span>}
                    </span>
                    <span className="fo-meta">{c.mac} · {c.max_rssi} dBm · {ago(c.last_seen_at)}</span>
                    <div className="fo-actions">
                      <button type="button" className="fo-chip" onClick={() => setPick({ type: "camera", id: c.id })}>
                        Found it, pin it
                      </button>
                      <button type="button" className="fo-chip" onClick={() => markAlpr(c)}>
                        {c.category === "alpr" ? "Not a plate reader" : "It's a plate reader"}
                      </button>
                    </div>
                  </li>
                ))}
              </ul>
            )}
            <p className="fo-meta" style={{ marginTop: 8 }}>
              Plate readers are shared with everyone. Other cameras stay private to you.
            </p>
          </section>
            </>
          ) : (
          <section>
            <h2>Put your house on the map</h2>
            <p className="fo-note">
              A ~$15 box in your window adds your sky to this map. You'll need a login to register it.
            </p>
            <div className="fo-actions" style={{ marginTop: 8 }}>
              <Link to={FO_ABOUT + "#kit"} className="fo-chip" style={{ textDecoration: "none" }}>Get a box</Link>
              <Link to={FO_ABOUT + "#join"} className="fo-chip" style={{ textDecoration: "none" }}>Ask for a login</Link>
              {!loggedIn && <Link to="/login" className="fo-chip" style={{ textDecoration: "none" }}>Log in</Link>}
            </div>
          </section>
          )}
        </aside>
      </div>
    </div>
  );
}

export default FlockoutMap;
